import axios from 'axios';
import { getTokenFromCookie } from '../utils/tokenHelper';
import config from '../data/config';
export const getApplicationByApplicantGuid = async (applicantGuid: string) => {
    try{
        const response = await axios.get(`/api/v1/interview/${applicantGuid}`);
        return response.data;
    } catch (error) {
        console.log('Error fetching application:', error);
    }
};

export const getAppByAppGuid = async (appGuid: string) => {
    try {
        const response = await axios.get(`/api/v1/interview/app/${appGuid}`);
        return response.data;
    } catch (error) {
        console.log('Error fetching app by appGuid:', error);
        throw error;
    }
};

export const getApplicationByJobId = async (jobId: string) => {
    try{
        // Note: This endpoint is now public and doesn't require authentication
        const response = await axios.get(`/api/v1/job/${jobId}`);
        return response.data;
    } catch (error) {
        console.log('Error fetching job data:', error);
        // Re-throw the error so the calling code can handle it properly
        throw error;
    }
};

export const initializeAnswersForInterview = async (applicantGuid: string) => {
    try{
        const response = await axios.post(`/api/v1/answer/${applicantGuid}/initialize`);
        console.log('Initialized answers for applicantGuid', applicantGuid);
        return response.data;
    } catch (error) {
        console.log('Error saving interview:', error);
    }
};

export const saveInterview = async (payload: any) => {
    try{
        // console.log('received payload', payload);
        const interviewStatus = {
            status: "Completed"
          };
        await axios.patch(`/api/v1/interview/${payload.applicantGuid}`, interviewStatus);
        const response = await axios.post(`/api/v1/interview/responses`, payload);
        return response.data;
    } catch (error) {
        console.log('Error saving interview:', error);
    }
};

export interface PaginationParams {
    page?: number;
    limit?: number;
    status?: 'Completed' | 'Incomplete' | 'Awaiting Action';
    // VI-753: optional server-side date-range (+ job) filters for the list path,
    // mirroring SubmissionCountsParams so the grid and the badge query identically.
    startDate?: Date;
    endDate?: Date;
    jobId?: number;
}

export interface InterviewsApiResponse {
  success: boolean;
  data: any[];
  total?: number;
}

// Get interviews by company ID with pagination
// VI-753: dropped the dead 3rd positional `status?` arg (it was shadowed by the
// `status` destructured from `params`). All filters now live on `params`, and the
// optional date-range (+ jobId) is forwarded so the grid uses the same
// server-side, date-bounded query as the counts badge.
export const getInterviewsByCompanyId = async (
    companyId: string | number,
    params: PaginationParams = {}
  ): Promise<InterviewsApiResponse> => {
    try {
      const { page = 1, limit = 20, status, startDate, endDate, jobId } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (status) {
        queryParams.append('status', status as string);
      }

      // VI-753: optional date-range + job filters (mirror getInterviewSubmissionCounts)
      if (startDate) {
        queryParams.append('start_date', startDate.toISOString());
      }
      if (endDate) {
        queryParams.append('end_date', endDate.toISOString());
      }
      if (jobId !== undefined && jobId !== null) {
        queryParams.append('jobId', jobId.toString());
      }

      // const response = await axios.get(
      //   `http://localhost:3005/api/v1/application/company/${companyId}/interviews?${queryParams.toString()}`
      // );

      const token = getTokenFromCookie();
      const response = await axios.get<InterviewsApiResponse>(
        `/api/v1/company/${companyId}/interviews?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching interviews by companyId:', error);
      throw error;
    }
  };

// VI-623 / AS-8108 — Video Screener submission counts grouped by status.
export interface SubmissionCounts {
  completed: number;
  incomplete: number;
  awaitingAction: number;
}

export interface SubmissionCountsResponse {
  companyId: number;
  jobId?: number;
  counts: SubmissionCounts;
}

export interface SubmissionCountsParams {
  jobId?: number;
  startDate: Date;
  endDate: Date;
}

// Get Video Screener submission counts by status for a company (optionally a
// single job) within a required date range. Backed by the BFF passthrough to
// the JobApplication API counts endpoint.
export const getInterviewSubmissionCounts = async (
    companyId: string | number,
    params: SubmissionCountsParams
  ): Promise<SubmissionCountsResponse> => {
    try {
      const { jobId, startDate, endDate } = params;

      const queryParams = new URLSearchParams({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      });

      if (jobId !== undefined && jobId !== null) {
        queryParams.append('jobId', jobId.toString());
      }

      const token = getTokenFromCookie();
      const response = await axios.get<SubmissionCountsResponse>(
        `/api/v1/company/${companyId}/interviews/counts?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching interview submission counts:', error);
      throw error;
    }
  };

export const getInterviewsByJobId = async (
    jobId: number,
    params: PaginationParams = {},
    status?: string
  ): Promise<InterviewsApiResponse> => {
    try {
      // VI-711: `status` may arrive either as the 3rd arg (JobsPage) or inside
      // `params` (useInterviewManagement). Previously only the 3rd arg was read,
      // so the Responses grid's `params.status = 'Completed'` was silently
      // dropped — the job view fetched the newest 100 of ALL statuses and the
      // grid could show 0 while the badge (server completed count) showed N.
      const { page = 1, limit = 100, status: paramStatus } = params;
      const effectiveStatus = status ?? paramStatus;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (effectiveStatus) {
        queryParams.append('status', effectiveStatus as string);
      }

      const token = getTokenFromCookie();
      const response = await axios.get<InterviewsApiResponse>(
        `/api/v1/job/${jobId}/interviews?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching interviews by jobId ${jobId}:`, error);
      throw error;
    }
  };